import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import API_BASE_URL from '../../config/api';

export default function FloatingCortex() {
  const [chatOpen, setChatOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [proximity, setProximity] = useState(0);
  const [messages, setMessages] = useState([
    { from: 'cortex', text: '¡Bienvenido! Soy CORTEX, su concierge financiero inteligente. Estoy aquí para guiarle en todo lo que necesite.' },
    { from: 'cortex', text: 'Cuento con un equipo de 6 agentes IA especializados: AEGIS (soporte), CIPHER (tech), SENTINEL (compliance), QUANTUM (FX), NEXUS (onboarding) y ORACLE (analytics).' },
    { from: 'cortex', text: 'Escriba /aegis, /sentinel, /quantum o el nombre de cualquier servicio y le conecto directamente con la IA indicada. ¿En qué puedo servirle?' },
  ]);
  const [input, setInput] = useState('');
  const [activeAgent, setActiveAgent] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const chatEndRef = useRef(null);
  const cortexRef = useRef(null);
  const proximityRafRef = useRef(null);

  // Listen for agent click events from AIMascots
  useEffect(() => {
    function handleAgentChat(e) {
      const agent = e.detail.agent;
      setActiveAgent(agent);
      setConversationId(null);
      setChatOpen(true);
      setMessages([
        { from: 'agent', text: `¡Hola! Soy ${agent.name}, ${agent.desc.toLowerCase()} ¿En qué puedo ayudarle hoy?`, agentName: agent.name, agentColor: agent.color },
      ]);
    }
    window.addEventListener('openAgentChat', handleAgentChat);
    return () => window.removeEventListener('openAgentChat', handleAgentChat);
  }, []);

  // Mouse proximity effect for sparkle emphasis.
  useEffect(() => {
    function computeProximity(clientX, clientY) {
      if (!cortexRef.current) return;
      const rect = cortexRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const distance = Math.hypot(clientX - centerX, clientY - centerY);
      const triggerRadius = 220;
      const normalized = Math.max(0, 1 - distance / triggerRadius);
      setProximity(normalized);
    }

    function handleMouseMove(e) {
      if (proximityRafRef.current) return;
      const { clientX, clientY } = e;
      proximityRafRef.current = window.requestAnimationFrame(() => {
        computeProximity(clientX, clientY);
        proximityRafRef.current = null;
      });
    }

    function resetProximity() {
      setProximity(0);
    }

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('blur', resetProximity);
    return () => {
      if (proximityRafRef.current) {
        window.cancelAnimationFrame(proximityRafRef.current);
        proximityRafRef.current = null;
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('blur', resetProximity);
    };
  }, []);

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const userMsg = input.trim().slice(0, 4000);
    setMessages(prev => [...prev, { from: 'user', text: userMsg }]);
    setInput('');
    setIsLoading(true);

    try {
      // Map agent name to valid backend agent: only 'aegis' and 'cortex' are active
      let agentName = 'aegis';
      if (activeAgent) {
        const name = activeAgent.name.toLowerCase();
        agentName = name === 'cortex' ? 'cortex' : 'aegis';
      }

      const res = await fetch(`${API_BASE_URL}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent: agentName,
          message: userMsg,
          ...(conversationId && { conversationId }),
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setConversationId(data.conversationId);

      const msgFrom = activeAgent ? 'agent' : 'cortex';
      setMessages(prev => [...prev, {
        from: msgFrom,
        text: data.response,
        ...(activeAgent && { agentName: activeAgent.name, agentColor: activeAgent.color }),
      }]);
    } catch (error) {
      console.error('AI chat error:', error);
      setMessages(prev => [...prev, {
        from: activeAgent ? 'agent' : 'cortex',
        text: 'Lo siento, estoy experimentando dificultades tecnicas. Por favor intente nuevamente.',
        ...(activeAgent && { agentName: activeAgent.name, agentColor: activeAgent.color }),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const sparkleBoost = Math.max(proximity, hovered ? 1 : 0);
  const orbitRadius = 24 + sparkleBoost * 8;
  const particleScale = 0.9 + sparkleBoost * 0.35;
  const auraScaleMax = 1.2 + sparkleBoost * 0.2;

  return (
    <>
      {/* ── Floating CORTEX mascot ── */}
      <motion.div
        ref={cortexRef}
        className="fixed z-50 cursor-pointer select-none"
        style={{ right: '1.5rem', bottom: '5.75rem' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => setChatOpen(true)}
      >
        <motion.div
          className="relative w-14 h-14"
          animate={{ y: [0, -8, 0], rotate: [0, 2, 0, -2, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        >
          {/* Outer aura — mystical ring */}
          <motion.div
            className="absolute -inset-4 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(210,60,179,0.2), rgba(168,67,151,0.05), transparent)' }}
            animate={{ scale: [1, auraScaleMax, 1], opacity: [0.4, 0.15, 0.4] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Orbiting particles */}
          {[0, 1, 2, 3].map((i) => (
            <motion.div
              key={i}
              className="absolute w-[3px] h-[3px] rounded-full"
              style={{
                background: i % 2 === 0 ? '#D23CB3' : '#A84397',
                boxShadow: `0 0 6px ${i % 2 === 0 ? 'rgba(210,60,179,0.8)' : 'rgba(168,67,151,0.8)'}`,
                top: '50%',
                left: '50%',
              }}
              animate={{
                x: [
                  Math.cos((i / 4) * Math.PI * 2) * orbitRadius,
                  Math.cos((i / 4) * Math.PI * 2 + Math.PI) * orbitRadius,
                  Math.cos((i / 4) * Math.PI * 2) * orbitRadius,
                ],
                y: [
                  Math.sin((i / 4) * Math.PI * 2) * orbitRadius,
                  Math.sin((i / 4) * Math.PI * 2 + Math.PI) * orbitRadius,
                  Math.sin((i / 4) * Math.PI * 2) * orbitRadius,
                ],
                scale: [particleScale * 0.9, particleScale, particleScale * 0.9],
              }}
              transition={{ duration: 6 + i, repeat: Infinity, ease: 'linear' }}
            />
          ))}

          {/* Inner glow */}
          <div
            className="absolute inset-1 rounded-full blur-[8px] opacity-40"
            style={{ background: 'radial-gradient(circle, rgba(210,60,179,0.6), transparent)' }}
          />

          {/* The CrossPay logo — the soul of CORTEX */}
          <motion.img
            src="/logo-icon.png"
            alt=""
            className="w-14 h-14 object-contain relative z-10 select-none"
            style={{ filter: 'drop-shadow(0 0 12px rgba(210,60,179,0.5)) drop-shadow(0 0 25px rgba(168,67,151,0.25))' }}
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Hover tooltip — wrapper sin transform de Framer: si el motion.div anima y/scale,
              su transform pisa el -translate-x-1/2 de Tailwind y el label queda descentrado a la derecha. */}
          <AnimatePresence>
            {hovered && !chatOpen && (
              <div className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 flex w-max max-w-[min(100vw-1rem,12rem)] -translate-x-1/2 justify-center">
                <motion.div
                  className="rounded-full border border-purple-primary/30 px-3 py-1.5 whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg, rgba(168,67,151,0.9), rgba(210,60,179,0.9))' }}
                  initial={{ opacity: 0, y: 5, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 5, scale: 0.9 }}
                >
                  <span className="text-center text-[10px] font-bold font-headline tracking-wide text-white">CORTEX</span>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>

      {/* ── Chat modal ── */}
      <AnimatePresence>
        {chatOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setChatOpen(false)}
            />

            {/* Chat window */}
            <motion.div
              className="fixed z-[70] bottom-3 left-3 right-3 w-auto max-w-[380px] sm:left-auto sm:right-6 sm:bottom-6 sm:ml-auto sm:mr-0 sm:w-[380px] sm:max-w-none rounded-2xl border border-purple-primary/30 overflow-hidden shadow-2xl"
              style={{ background: 'linear-gradient(180deg, #141216 0%, #0a090c 100%)' }}
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25 }}
            >
              {/* Header — shows active agent or CORTEX */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-purple-primary/15">
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{ background: activeAgent ? activeAgent.color : 'linear-gradient(135deg, #A84397, #D23CB3)' }}
                  >
                    {activeAgent ? (
                      <span className="material-symbols-outlined text-white text-[20px]">{activeAgent.icon}</span>
                    ) : (
                      <img src="/logo-icon.png" alt="" className="w-6 h-6 object-contain" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-bold font-headline text-white">{activeAgent ? activeAgent.name : 'CORTEX'}</p>
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                      <span className="text-[9px] text-green-400/80 font-label uppercase tracking-wider">
                        {activeAgent ? activeAgent.role : 'Concierge Financiero'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {activeAgent && (
                    <button
                      onClick={() => {
                        setActiveAgent(null);
                        setConversationId(null);
                        setMessages([
                          { from: 'cortex', text: '¡Bienvenido de vuelta! Soy CORTEX, su concierge financiero. ¿En qué más puedo ayudarle?' },
                        ]);
                      }}
                      className="w-8 h-8 rounded-lg hover:bg-white/5 flex items-center justify-center transition-colors"
                      title="Volver a CORTEX"
                    >
                      <span className="material-symbols-outlined text-on-surface-variant/60 text-[16px]">arrow_back</span>
                    </button>
                  )}
                  <button
                    onClick={() => { setChatOpen(false); setActiveAgent(null); setConversationId(null); }}
                    className="w-8 h-8 rounded-lg hover:bg-white/5 flex items-center justify-center transition-colors"
                  >
                    <span className="material-symbols-outlined text-on-surface-variant/60 text-[18px]">close</span>
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="h-[320px] overflow-y-auto px-5 py-4 space-y-4 scrollbar-thin">
                {messages.map((msg, i) => (
                  <motion.div
                    key={i}
                    className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className={`max-w-[80%] rounded-2xl text-sm leading-relaxed ${
                      msg.from === 'user'
                        ? 'bg-gradient-to-r from-purple-primary to-accent-magenta text-white rounded-br-md px-4 py-2.5'
                        : 'bg-white/[0.06] text-on-surface-variant border border-white/[0.04] rounded-bl-md px-4 py-2.5'
                    }`}>
                      {msg.agentName && (
                        <span className="text-[10px] font-bold font-label uppercase tracking-wider block mb-1" style={{ color: msg.agentColor || '#D23CB3' }}>
                          {msg.agentName}
                        </span>
                      )}
                      {msg.text}
                    </div>
                  </motion.div>
                ))}
                {isLoading && (
                  <motion.div
                    className="flex justify-start"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="bg-white/[0.06] border border-white/[0.04] rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1.5">
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          className="w-2 h-2 rounded-full bg-purple-primary/60"
                          animate={{ y: [0, -6, 0] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSend} className="px-4 py-3 border-t border-purple-primary/10">
                <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.06] rounded-xl px-4 py-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={isLoading ? 'Pensando...' : 'Escriba su mensaje...'}
                    disabled={isLoading}
                    maxLength={4000}
                    autoComplete="off"
                    className="flex-1 bg-transparent text-sm text-white placeholder-gray-600 outline-none disabled:opacity-50"
                  />
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-8 h-8 rounded-lg bg-gradient-to-r from-purple-primary to-accent-magenta flex items-center justify-center hover:shadow-lg hover:shadow-purple-primary/20 transition-shadow disabled:opacity-50"
                  >
                    <span className="material-symbols-outlined text-white text-[16px]">send</span>
                  </button>
                </div>
                <p className="text-[9px] text-on-surface-variant/30 text-center mt-2 font-label">
                  Potenciado por CrossPay IA
                </p>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
